/**
 * Phrase tracks for THE EDGE — Guided Performance Rituals.
 *
 * Every `start` is the *exact* second a phrase is spoken in the
 * recording (verified with OpenAI Whisper segment timestamps). The
 * `tone` is used by the overlay to choose styling:
 *   - 'soft'      → gentle/breath instruction
 *   - 'identity'  → I-statement, larger + warmer glow
 *   - 'imagine'   → visualisation cue
 *   - 'closing'   → final integration
 *
 * Editing rule: keep `start` ascending. The overlay picks the latest
 * phrase whose `start <= currentTime` and shows it for the natural
 * gap until the next phrase starts.
 */

export const EDGE_PHRASES = {
  edge_instant_manifestation: [
    { start: 1,   text: 'Close your eyes',                              tone: 'soft'     },
    { start: 4,   text: 'Take a slow breath in',                        tone: 'soft'     },
    { start: 9,   text: '\u2026and release',                            tone: 'soft'     },
    { start: 14,  text: 'Let everything settle',                        tone: 'soft'     },
    { start: 18,  text: 'Now imagine this',                             tone: 'imagine'  },
    { start: 22,  text: 'You wake up',                                  tone: 'imagine'  },
    { start: 25,  text: 'Before you even move\u2026 you feel it',       tone: 'imagine'  },
    { start: 29,  text: 'Something has shifted',                        tone: 'imagine'  },
    { start: 33,  text: 'Check your phone',                             tone: 'imagine'  },
    { start: 40,  text: 'Your team is active',                          tone: 'imagine'  },
    { start: 44,  text: 'People are stepping up',                       tone: 'imagine'  },
    { start: 48,  text: 'New people are joining',                       tone: 'imagine'  },
    { start: 52,  text: 'Momentum is building',                         tone: 'imagine'  },
    { start: 56,  text: 'You\u2019re not chasing anymore',              tone: 'identity' },
    { start: 60,  text: 'You\u2019re leading',                          tone: 'identity' },
    { start: 63,  text: 'Naturally',                                    tone: 'identity' },
    { start: 66,  text: 'Feel that',                                    tone: 'soft'     },
    { start: 69,  text: 'You get on a call',                            tone: 'imagine'  },
    { start: 71,  text: 'Your presence is different',                   tone: 'identity' },
    { start: 75,  text: 'Calm',                                         tone: 'identity' },
    { start: 78,  text: 'Certain',                                      tone: 'identity' },
    { start: 81,  text: 'Grounded',                                     tone: 'identity' },
    { start: 84,  text: 'People listen',                                tone: 'imagine'  },
    { start: 88,  text: 'People trust you',                             tone: 'identity' },
    { start: 92,  text: 'People follow your energy',                    tone: 'identity' },
    { start: 96,  text: 'You don\u2019t need to convince',              tone: 'soft'     },
    { start: 99,  text: 'You don\u2019t need to push',                  tone: 'soft'     },
    { start: 103, text: 'You simply are the example',                   tone: 'identity' },
    { start: 108, text: 'Your whole team feels it',                     tone: 'imagine'  },
    { start: 112, text: 'They duplicate it',                            tone: 'imagine'  },
    { start: 116, text: 'Now, see it expand',                           tone: 'imagine'  },
    { start: 122, text: 'More leaders rising under you',                tone: 'imagine'  },
    { start: 127, text: 'More consistency',                             tone: 'imagine'  },
    { start: 131, text: 'More flow',                                    tone: 'imagine'  },
    { start: 135, text: 'Your business feels alive',                    tone: 'identity' },
    { start: 139, text: 'Stable',                                       tone: 'identity' },
    { start: 142, text: 'Growing',                                      tone: 'identity' },
    { start: 145, text: 'And you can handle it',                        tone: 'identity' },
    { start: 148, text: 'You\u2019re built for this',                   tone: 'identity' },
    { start: 151, text: 'Just breathe that in',                         tone: 'soft'     },
    { start: 156, text: 'This is not something you\u2019re chasing',    tone: 'identity' },
    { start: 160, text: 'This is something you are stepping into now',  tone: 'identity' },
    { start: 167, text: 'Now let it land',                              tone: 'closing'  },
    { start: 171, text: 'This is me',                                   tone: 'closing'  },
    { start: 174, text: 'This is my reality',                           tone: 'closing'  },
    { start: 178, text: 'This is already unfolding',                    tone: 'closing'  },
    { start: 183, text: 'Slowly breathe in',                            tone: 'soft'     },
    { start: 189, text: '\u2026and release',                            tone: 'closing'  },
  ],

  edge_future_team: [
    { start: 1,   text: 'Close your eyes',                              tone: 'soft'     },
    { start: 4,   text: 'Breathe in',                                   tone: 'soft'     },
    { start: 10,  text: '\u2026and release',                            tone: 'soft'     },
    { start: 15,  text: 'Now imagine this',                             tone: 'imagine'  },
    { start: 19,  text: 'Your team',                                    tone: 'imagine'  },
    { start: 23,  text: 'Growing',                                      tone: 'imagine'  },
    { start: 27,  text: 'New people joining',                           tone: 'imagine'  },
    { start: 31,  text: 'Energy building',                              tone: 'imagine'  },
    { start: 35,  text: 'But more than that\u2026',                     tone: 'imagine'  },
    { start: 38,  text: 'Leaders emerging',                             tone: 'identity' },
    { start: 42,  text: 'People stepping up',                           tone: 'identity' },
    { start: 46,  text: 'Taking ownership',                             tone: 'identity' },
    { start: 50,  text: 'Duplicating what you do',                      tone: 'identity' },
    { start: 54,  text: 'You\u2019re not carrying it all',              tone: 'identity' },
    { start: 57,  text: 'You\u2019re guiding it',                       tone: 'identity' },
    { start: 60,  text: 'Feel the difference',                          tone: 'soft'     },
    { start: 64,  text: 'There is space',                               tone: 'identity' },
    { start: 68,  text: 'There is support',                             tone: 'identity' },
    { start: 71,  text: 'There is flow',                                tone: 'identity' },
    { start: 75,  text: 'Your organisation is alive',                   tone: 'identity' },
    { start: 80,  text: 'And you trust it',                             tone: 'identity' },
    { start: 83,  text: 'You trust yourself to lead it',                tone: 'identity' },
    { start: 87,  text: 'Now expand that',                              tone: 'imagine'  },
    { start: 91,  text: 'Bigger team',                                  tone: 'imagine'  },
    { start: 94,  text: 'Stronger leaders',                             tone: 'imagine'  },
    { start: 98,  text: 'More consistency',                             tone: 'imagine'  },
    { start: 102, text: 'And you remain calm within it',                tone: 'identity' },
    { start: 107, text: 'This is natural for you',                      tone: 'closing'  },
    { start: 110, text: 'Take a breath',                                tone: 'soft'     },
    { start: 114, text: 'And let it land',                              tone: 'closing'  },
  ],

  edge_wealth_normalisation: [
    { start: 1,   text: 'Breathe in',                                   tone: 'soft'     },
    { start: 7,   text: '\u2026and release',                            tone: 'soft'     },
    { start: 10,  text: 'Relax your body',                              tone: 'soft'     },
    { start: 14,  text: 'Now, imagine numbers',                         tone: 'imagine'  },
    { start: 18,  text: 'Larger numbers',                               tone: 'imagine'  },
    { start: 22,  text: 'At first, just observe',                       tone: 'soft'     },
    { start: 26,  text: 'No reaction',                                  tone: 'soft'     },
    { start: 31,  text: 'Now soften into it',                           tone: 'soft'     },
    { start: 35,  text: 'Open up to it',                                tone: 'identity' },
    { start: 39,  text: 'This is safe',                                 tone: 'identity' },
    { start: 42,  text: 'This is normal',                               tone: 'identity' },
    { start: 46,  text: 'You are someone who handles this',             tone: 'identity' },
    { start: 50,  text: 'Money flows in',                               tone: 'identity' },
    { start: 54,  text: 'Money flows through',                          tone: 'identity' },
    { start: 58,  text: 'Money grows',                                  tone: 'identity' },
    { start: 62,  text: 'Without stress',                               tone: 'soft'     },
    { start: 66,  text: 'Without pressure',                             tone: 'soft'     },
    { start: 69,  text: 'You expand with it',                           tone: 'identity' },
    { start: 73,  text: 'Just feel that for a moment',                  tone: 'soft'     },
    { start: 79,  text: 'This is your new reality',                     tone: 'closing'  },
    { start: 85,  text: 'Take a breath',                                tone: 'soft'     },
    { start: 89,  text: 'And lock it in',                               tone: 'closing'  },
  ],

  // Hybrid track. The intro plays once (system voice), then the loop
  // plays continuously (identity affirmations).
  edge_daily_money_identity: [
    { start: 1,   text: 'Take a slow breath in',                        tone: 'soft'     },
    { start: 8,   text: '\u2026and gently release',                     tone: 'soft'     },
    { start: 12,  text: 'Again',                                        tone: 'soft'     },
    { start: 14,  text: 'Slowly breathe in',                            tone: 'soft'     },
    { start: 19,  text: '\u2026and release',                            tone: 'soft'     },
    { start: 23,  text: 'Let your shoulders drop',                      tone: 'soft'     },
    { start: 26,  text: 'Let your body soften',                         tone: 'soft'     },
    { start: 30,  text: 'Nothing to chase',                             tone: 'soft'     },
    { start: 33,  text: 'Nothing to force',                             tone: 'soft'     },
    { start: 37,  text: 'Just arrive',                                  tone: 'identity' },
    { start: 41,  text: 'Now, begin to feel this\u2026',                tone: 'imagine'  },
  ],

  // Loop affirmations — these play continuously. Timestamps are
  // relative to the *loop track* (33s). The overlay rebases to loop
  // currentTime when it's playing.
  edge_daily_money_identity_loop: [
    { start: 1,   text: 'I lead with clarity',                          tone: 'identity' },
    { start: 5,   text: 'I move with confidence',                       tone: 'identity' },
    { start: 9,   text: 'I create momentum',                            tone: 'identity' },
    { start: 13,  text: 'I build strong teams',                         tone: 'identity' },
    { start: 17,  text: 'I am trusted',                                 tone: 'identity' },
    { start: 20,  text: 'I am consistent',                              tone: 'identity' },
    { start: 23,  text: 'I follow through',                             tone: 'identity' },
    { start: 26,  text: 'I expand daily',                               tone: 'identity' },
    { start: 29,  text: 'I am becoming more',                           tone: 'closing'  },
  ],
};
